import { AxiosRequestConfig, AxiosResponse, ResponseType } from 'axios';
import { AxiosProgressEvent } from '../types/AxiosProgressEvent';
import { IRequestBuilder } from '../types/RequestBuilder';
import http from './axiosInstance';

const endSlash = new RegExp(/\/$/);
const startSlash = new RegExp(/^\//);

function stringToUtf16Bytes(str: string): Uint8Array {
  const utf16 = new Uint16Array(str.length);
  for (let i = 0; i < str.length; i++) {
    utf16[i] = str.charCodeAt(i); // UTF-16 code units
  }
  return new Uint8Array(utf16.buffer); // Convert buffer to byte array
}

function uint8ToBase64(uint8Array: Uint8Array): string {
  let binary = '';
  uint8Array.forEach((byte) => (binary += String.fromCharCode(byte)));
  return btoa(binary);
}

//Regex to check for special characters (excluding space and dash)
//Excluded space and dashes as they are commonly used in id, SchoolId etc
const specialCharRegex = /[^a-zA-Z0-9 -]/;

function encodeValue(key: string, value: unknown): unknown {
  if (
    //only encrypt strings, that are not date fields and have special characters
    typeof value === 'string' &&
    !key.toLowerCase().includes('date') &&
    specialCharRegex.test(value)
  ) {
    const utf16Bytes = stringToUtf16Bytes(value);
    return uint8ToBase64(utf16Bytes);
  }
  if (Array.isArray(value)) {
    return value.map((item) => encodeValue(key, item));
  }
  if (value && typeof value === 'object') {
    const encodedObj: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    Object.keys(encodedObj).forEach((nestedKey) => {
      encodedObj[nestedKey] = encodeValue(nestedKey, encodedObj[nestedKey]);
    });
    return encodedObj;
  }
  return value;
}

function encodeStringAttributes<T extends Record<string, unknown>>(obj: T): T {
  const encodedObj: Record<string, unknown> = { ...obj };
  Object.keys(encodedObj).forEach((key) => {
    encodedObj[key] = encodeValue(key, encodedObj[key]);
  });
  return encodedObj as T;
}

const assembleUrl = (base: string) => (path: string) => {
  const cleanBase = base.replace(endSlash, '');
  const cleanPath = path.replace(startSlash, '');

  return `${cleanBase}/${cleanPath}`;
};

export const shouldValidateStatus = (status: number): boolean => {
  // Don't hide results for requests with status under 600
  return status < 600;
};

export const buildUrl = (base: string, path: string): string =>
  assembleUrl(base)(path);

const createRequestConfig = (
  bearerToken?: string,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  responseType?: ResponseType,
  abortSignal?: AbortSignal
): AxiosRequestConfig => {
  const config: AxiosRequestConfig = {
    headers: {},
    validateStatus: shouldValidateStatus,
  };

  if (bearerToken) {
    config.headers = {
      Authorization: `Bearer ${bearerToken}`,
    };
  }

  if (onUploadProgress) {
    config.onUploadProgress = onUploadProgress;
  }

  if (responseType) {
    config.responseType = responseType;
  }

  if (abortSignal) {
    config.signal = abortSignal;
  }

  return config;
};

const axiosRequestBuilder = (
  apiBasePath: string,
  relativePath: string,
  bearerToken?: string,
  abortSignal?: AbortSignal,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  responseType?: ResponseType
): IRequestBuilder => {
  return {
    url: buildUrl(apiBasePath, relativePath),
    requestConfig: createRequestConfig(
      bearerToken,
      onUploadProgress,
      responseType,
      abortSignal
    ),
  };
};

export class HttpClient {
  apiBasePath: string;
  bearerToken?: string;
  abortSignal?: AbortSignal;

  constructor(
    apiBasePath: string,
    bearerToken?: string,
    abortSignal?: AbortSignal
  ) {
    this.apiBasePath = apiBasePath;
    this.bearerToken = bearerToken;
    this.abortSignal = abortSignal;
  }

  async get<T>(relativePath: string): Promise<AxiosResponse<T>> {
    const { url, requestConfig } = axiosRequestBuilder(
      this.apiBasePath,
      relativePath,
      this.bearerToken,
      this.abortSignal
    );

    return await http.get<T>(url, requestConfig);
  }

  async getFile<T>(relativePath: string): Promise<AxiosResponse<T>> {
    const { url, requestConfig } = axiosRequestBuilder(
      this.apiBasePath,
      relativePath,
      this.bearerToken,
      this.abortSignal,
      undefined,
      'blob'
    );

    return await http.get<T>(url, requestConfig);
  }

  async post<T>(
    relativePath: string,
    data?: Record<string, string | string[] | undefined> | Object
  ): Promise<AxiosResponse<T>> {
    const { url, requestConfig } = axiosRequestBuilder(
      this.apiBasePath,
      relativePath,
      this.bearerToken,
      this.abortSignal
    );

    let encodedData = data;
    if (
      data &&
      typeof data === 'object' &&
      relativePath != 'token' &&
      !(data instanceof FormData) &&
      !Array.isArray(data)
    ) {
      encodedData = encodeStringAttributes(data as Record<string, unknown>);
    }
    return await http.post<T>(url, encodedData, requestConfig);
  }

  async put<T>(relativePath: string, data?: Object): Promise<AxiosResponse<T>> {
    const { url, requestConfig } = axiosRequestBuilder(
      this.apiBasePath,
      relativePath,
      this.bearerToken,
      this.abortSignal
    );

    let encodedData = data;
    if (
      data &&
      typeof data === 'object' &&
      !(data instanceof FormData) &&
      !Array.isArray(data)
    ) {
      encodedData = encodeStringAttributes(data as Record<string, unknown>);
    }
    return await http.put<T>(url, encodedData, requestConfig);
  }

  async delete<T>(relativePath: string): Promise<AxiosResponse<T>> {
    const { url, requestConfig } = axiosRequestBuilder(
      this.apiBasePath,
      relativePath,
      this.bearerToken,
      this.abortSignal
    );

    return await http.delete<T>(url, requestConfig);
  }

  async upload<T>(
    relativePath: string,
    data?: FormData,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<AxiosResponse<T>> {
    const { url, requestConfig } = axiosRequestBuilder(
      this.apiBasePath,
      relativePath,
      this.bearerToken,
      this.abortSignal,
      onUploadProgress
    );

    return await http.post<T>(url, data, requestConfig);
  }
}

export const getHttpClient = (
  apiBasePath: string,
  bearerToken: string,
  abortSignal?: AbortSignal
): HttpClient => {
  return new HttpClient(apiBasePath, bearerToken, abortSignal);
};

export const getAnonymousHttpClient = (
  apiBasePath: string,
  abortSignal?: AbortSignal
): HttpClient => {
  return new HttpClient(apiBasePath, undefined, abortSignal);
};
